import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CTA_TYPES, CTA_TYPE_UI_LABELS, type CtaType, type OfferConfig } from '@dealcloser/shared'
import { useApi } from '@/hooks/use-api'
import { useUpdateCampaign } from './use-campaigns'
import {
  ResourcesEditor,
  SettingsLinkSuggestion,
  cleanResourceRows,
  resourceRowsValid,
  resourcesToRows,
  type ResourceRow,
} from './campaign-resources'
import type { Campaign } from './types'
import { parseCampaignSettings } from '@/features/settings/mcp-helpers'
import { retargetTemplateLink, templatesUseLink } from './templates/template-fill'

type EditCampaignDialogProps = {
  readonly campaign: Campaign
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

const URL_PATTERN = /^https?:\/\/\S+\.\S+/

export function EditCampaignDialog({
  campaign,
  open,
  onOpenChange,
}: EditCampaignDialogProps) {
  const [name, setName] = useState(campaign.name)
  // Campaigns created before the CTA became campaign-level (2026-08-30)
  // have no cta_type yet; default them to a demo booking so saving here
  // backfills the row.
  const [ctaType, setCtaType] = useState<CtaType>(campaign.cta_type ?? 'book_demo')
  const [offerLink, setOfferLink] = useState(campaign.offer_link ?? '')
  // Additional links & attachments (settings.resources) as editable rows.
  const [resources, setResources] = useState<readonly ResourceRow[]>(
    resourcesToRows(campaign.resources),
  )

  // The dialog stays mounted between opens, so re-seed the form from the
  // (possibly refetched) campaign each time it opens — otherwise a second
  // open after a save would show the pre-save values.
  useEffect(() => {
    if (!open) return
    setName(campaign.name)
    setCtaType(campaign.cta_type ?? 'book_demo')
    setOfferLink(campaign.offer_link ?? '')
    setResources(resourcesToRows(campaign.resources))
  }, [open, campaign])

  const updateCampaign = useUpdateCampaign()
  const apiFetch = useApi()

  // The link saved under Settings, Offer — offered as a one-click prefill
  // when this campaign has no link yet. Same query key as the wizard, so the
  // two surfaces share the cache.
  const { data: offerData } = useQuery({
    queryKey: ['settings', 'offer'],
    queryFn: async () => {
      const res = await apiFetch<OfferConfig>('/api/settings/offer')
      return res.success ? res.data : null
    },
    staleTime: 5 * 60_000,
    enabled: open,
  })

  const needsLink = ctaType !== 'direct_conversation'
  const trimmedLink = offerLink.trim()
  // The link is required for every call to action except Direct
  // Conversation, and must be a real URL because the AI pastes it verbatim.
  const linkValid = !needsLink || URL_PATTERN.test(trimmedLink)
  const linkInvalidShown = needsLink && trimmedLink !== '' && !URL_PATTERN.test(trimmedLink)
  const resourcesValid = resourceRowsValid(resources)

  // Template campaigns (card S3) have the link written into their messages,
  // and the send only allows the campaign's current link. A changed link is
  // carried into the messages on save; a removed one gets them held.
  const storedSteps = parseCampaignSettings(campaign.settings)['steps']
  const newLink = needsLink ? trimmedLink : ''
  const retargetedSteps = retargetTemplateLink(storedSteps, campaign.offer_link, newLink)
  const templatesLoseLink =
    templatesUseLink(storedSteps, campaign.offer_link) && newLink !== (campaign.offer_link ?? '').trim() && !retargetedSteps

  function handleSave() {
    // daily_limit is no longer user-editable (2026-07-27 owner directive) —
    // the platform manages sending volume; the stored value stays as-is.
    // `settings` is merged into the stored campaign settings JSON server-side,
    // so only the keys sent here change. A null offer_link removes the key.
    // `resources` is always sent (an empty array clears removed rows).
    updateCampaign.mutate(
      {
        id: campaign.id,
        updates: {
          name,
          settings: {
            cta_type: ctaType,
            offer_link: needsLink ? trimmedLink : null,
            resources: cleanResourceRows(resources),
            ...(retargetedSteps ? { steps: retargetedSteps } : {}),
          },
        },
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit Campaign</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="campaign-name">Name</Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Campaign name"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="campaign-cta-type">Call to action</Label>
            <Select value={ctaType} onValueChange={(v) => setCtaType(v as CtaType)}>
              <SelectTrigger id="campaign-cta-type">
                <SelectValue placeholder="What should leads do?" />
              </SelectTrigger>
              <SelectContent>
                {CTA_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{CTA_TYPE_UI_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              What the AI asks leads in this campaign to do.
            </p>
          </div>

          {needsLink ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="campaign-offer-link">Link the AI sends</Label>
              <SettingsLinkSuggestion
                settingsLink={offerData?.resourceLink}
                currentLink={offerLink}
                onUse={setOfferLink}
              />
              <Input
                id="campaign-offer-link"
                type="url"
                required
                value={offerLink}
                onChange={(e) => setOfferLink(e.target.value)}
                placeholder="https://"
              />
              <p className="text-xs text-muted-foreground">
                Pasted into the reply exactly as written when a lead in this
                campaign is ready for the next step.
              </p>
              {linkInvalidShown && (
                <p className="text-xs text-destructive">
                  Enter a full URL starting with https://
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No link is sent. The AI keeps the conversation going in LinkedIn.
            </p>
          )}

          {retargetedSteps && linkValid && (
            <p className="text-xs text-muted-foreground">
              Your template messages will use the new link too.
            </p>
          )}
          {templatesLoseLink && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Your template messages contain the old link. A message with a link the campaign no longer has is held
              for your review, where you can edit it before it sends.
            </p>
          )}

          <ResourcesEditor rows={resources} onChange={setResources} idPrefix="edit" />
        </div>

        <DialogFooter className="shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateCampaign.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              updateCampaign.isPending || !name.trim() || !linkValid || !resourcesValid
            }
          >
            {updateCampaign.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
